import { FormikProvider, useFormik } from 'formik'
import { Grid } from 'semantic-ui-react'
import { TaxSeasonKickoffSurveyProps } from './YearEndKickoffSurvey'
import { FormFlowFooter } from '@/components/form-flow/FormFlowFooter'
import {
  Alert,
  Button,
  Card,
  FormikLabelError,
  FormikRadioButton,
  getFieldNames,
  GridRowColumn,
  Link,
  makeReqBoolSchema,
  makeReqStringSchema,
  Text,
} from '@/componentsV1/BaseComponents'
import { useReselector } from '@/utils/sharedHooks'
import {
  selectCurrentAnnualTaxDetails,
  selectCurrentAnnualTaxYear,
} from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { getAnnualTaxFilingForYearSelector } from '../annualTaxFilings.selector'
import { needContractorFilingOption } from '../annualTaxFilings.slice'
import { TAX_SEASON_KICKOFF_SURVEY } from './TskTipsSideBar'
import { List } from '@/ui'
import { convertUtcToLocalDate, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { get1099NecThresholdDisplay } from '@/features/taxes/annual-taxes/constants'

const ContractorPayroll = ({
  goToNextStep,
  goBack,
}: TaxSeasonKickoffSurveyProps) => {
  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)
  const annualTaxDetails = useReselector(selectCurrentAnnualTaxDetails)

  const annualTaxFiling = useReselector(
    getAnnualTaxFilingForYearSelector,
    currentTaxYear
  )

  const form1099NECDueDate = convertUtcToLocalDate(
    annualTaxDetails?.form_1099_nec_due_date
  )?.toFormat(DATE_FORMATS_LUXON.DISPLAY_LONG)

  const formik = useFormik({
    initialValues: {
      paidContractorThisYear: annualTaxFiling?.paidContractorThisYear,
      needContractorFiling: annualTaxFiling?.needContractorFiling,
    },
    onSubmit: ({ paidContractorThisYear, needContractorFiling }) =>
      goToNextStep(
        {
          paidContractorThisYear,
          needContractorFiling:
            paidContractorThisYear === false
              ? needContractorFilingOption.no
              : needContractorFiling,
        },
        TAX_SEASON_KICKOFF_SURVEY.taxNeeds
      ),
  })

  const { submitForm, isSubmitting, values } = formik
  const fieldNames = getFieldNames(formik)

  return (
    <FormikProvider value={formik}>
      <Grid>
        <GridRowColumn
          width={12}
          columnStyle={{ display: 'flex', alignItems: 'center' }}
        >
          <Text as="display2" style={{ marginRight: 10 }}>
            Contractor tax payroll form
          </Text>
          <Text as="h2" color="darkGray">
            (1099-NEC)
          </Text>
        </GridRowColumn>
        <GridRowColumn width={12}>
          <Card type="subsection" backgroundColor="blush">
            <Text as="bodyLg">
              You only need to file a 1099-NEC in the following situations:
            </Text>
            <List>
              <List.Item>
                <Text as="bodyLg">
                  • You paid a contractor{' '}
                  <b>at least {get1099NecThresholdDisplay(currentTaxYear)}</b>{' '}
                  in {currentTaxYear} (clinical contractors, associate
                  therapists, billers, VAs, admin support, marketing/SEO
                  contractors).
                </Text>
              </List.Item>
              <List.Item>
                <Text as="bodyLg">
                  • You did <b>not</b> pay them through a payroll provider.
                </Text>
              </List.Item>
              <List.Item>
                <Text as="bodyLg">
                  • You paid them in cash, checks, or bank transfers (including
                  Venmo, CashApp or Zelle).
                </Text>
              </List.Item>
            </List>
            <Link
              newPage
              href="https://support.joinheard.com/hc/en-us/articles/18872262968727-Filing-1099-NEC-FAQs"
            >
              <Button variant="link">Learn more -&gt;</Button>
            </Link>
          </Card>
        </GridRowColumn>

        <GridRowColumn width={12}>
          <FormikLabelError
            name={fieldNames.paidContractorThisYear}
            label={`Did you pay a contractor in ${currentTaxYear} according to the terms above?`}
            schema={makeReqBoolSchema()}
          />
        </GridRowColumn>
        <GridRowColumn short width={12}>
          <FormikRadioButton
            name={fieldNames.paidContractorThisYear}
            label="Yes"
            value
            variant="default"
          />
        </GridRowColumn>
        <GridRowColumn short width={12}>
          <FormikRadioButton
            name={fieldNames.paidContractorThisYear}
            label="No"
            value={false}
            variant="default"
          />
        </GridRowColumn>

        {values.paidContractorThisYear === false && (
          <GridRowColumn short width={12}>
            <Card type="subsection" backgroundColor="natural">
              <Text as="bodyLg">
                Got it. You do not need to file a form 1099-NEC for{' '}
                {currentTaxYear}.
              </Text>
            </Card>
          </GridRowColumn>
        )}
        {values.paidContractorThisYear && (
          <>
            <GridRowColumn width={12}>
              <Alert type="announcement">
                <Text as="bodyMd">
                  Heard supports 1099-NEC filing through our trusted partner,{' '}
                  <Link
                    href="https://support.joinheard.com/hc/en-us/articles/19788518004887"
                    newPage
                  >
                    Taxfyle
                  </Link>
                  . You answer a few questions in Heard, a Taxfyle preparer
                  files your forms, and we cover the full cost.
                </Text>
              </Alert>
            </GridRowColumn>
            <GridRowColumn width={12}>
              <FormikLabelError
                name={fieldNames.needContractorFiling}
                label="Would you like to use Taxfyle to file your 1099-NECs?"
                schema={makeReqStringSchema()}
              />
            </GridRowColumn>
            <GridRowColumn short width={12}>
              <FormikRadioButton
                name={fieldNames.needContractorFiling}
                label="Yes"
                value={needContractorFilingOption.yes}
                variant="default"
              />
            </GridRowColumn>
            <GridRowColumn short width={12}>
              <FormikRadioButton
                name={fieldNames.needContractorFiling}
                label="No"
                value={needContractorFilingOption.no}
                variant="default"
              />
            </GridRowColumn>

            {values.needContractorFiling === needContractorFilingOption.yes && (
              <GridRowColumn short width={12}>
                <Card type="subsection" backgroundColor="natural">
                  <Text as="bodyLg">
                    Great. We&apos;ll add this item to your checklist so you can
                    answer a few questions before the deadline
                    {form1099NECDueDate && ` of ${form1099NECDueDate}`}.
                  </Text>
                </Card>
              </GridRowColumn>
            )}

            {values.needContractorFiling === needContractorFilingOption.no && (
              <GridRowColumn short width={12}>
                <Card type="subsection" backgroundColor="natural">
                  <Text as="bodyMd">
                    No problem! Check out{' '}
                    <Link
                      href="https://support.joinheard.com/hc/en-us/articles/16126138468247-What-is-Form-1099-NEC-and-how-to-file-it"
                      newPage
                    >
                      this article
                    </Link>{' '}
                    if you need help filing a 1099-NEC on your own.
                  </Text>
                </Card>
              </GridRowColumn>
            )}
          </>
        )}

        <FormFlowFooter
          onBack={() => goBack(TAX_SEASON_KICKOFF_SURVEY.taxEntity)}
          onForward={submitForm}
          continueDisabled={isSubmitting}
          loading={isSubmitting}
        />
      </Grid>
    </FormikProvider>
  )
}

export default ContractorPayroll
